package br.com.htms.inspeccaocampo.ui.screens

import br.com.htms.inspeccaocampo.ui.theme.PortalCores
import br.com.htms.inspeccaocampo.ui.kit.PortalOutlinedButton
import br.com.htms.inspeccaocampo.ui.kit.PortalButton
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Button
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import br.com.htms.inspeccaocampo.data.funcionarios.Cpf
import br.com.htms.inspeccaocampo.data.funcionarios.Funcionario
import br.com.htms.inspeccaocampo.data.funcionarios.FuncionariosApi
import br.com.htms.inspeccaocampo.data.funcionarios.FuncionariosPlanilha
import br.com.htms.inspeccaocampo.data.funcionarios.LinhaImportacao
import br.com.htms.inspeccaocampo.data.funcionarios.ResultadoImportacao
import br.com.htms.inspeccaocampo.form.AppController
import br.com.htms.inspeccaocampo.platform.PlatformFiles
import br.com.htms.inspeccaocampo.platform.PlatformServices
import kotlinx.coroutines.launch

/** Importação em lote de colaboradores a partir da planilha modelo (.xlsx/.csv). */
@OptIn(ExperimentalLayoutApi::class)
@Composable
internal fun ImportarFuncionariosPanel(
    controller: AppController,
    existentes: List<Funcionario>,
    onFechar: () -> Unit
) {
    val scope = rememberCoroutineScope()
    var lendo by remember { mutableStateOf(false) }
    var arquivo by remember { mutableStateOf("") }
    var resultado by remember { mutableStateOf<ResultadoImportacao?>(null) }
    var atualizarExistentes by remember { mutableStateOf(true) }
    var importando by remember { mutableStateOf(false) }
    var progresso by remember { mutableStateOf(0) }
    var total by remember { mutableStateOf(0) }
    var mensagem by remember { mutableStateOf<String?>(null) }
    var falhas by remember { mutableStateOf<List<String>>(emptyList()) }

    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceVariant)
    ) {
        Column(Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    "Importar colaboradores por planilha",
                    fontWeight = FontWeight.Bold,
                    style = MaterialTheme.typography.titleMedium,
                    modifier = Modifier.weight(1f),
                    color = PortalCores.Navy
                )
                TextButton(onClick = onFechar, enabled = !importando) { Text("Fechar") }
            }
            Text(
                "1) Baixe a planilha modelo. 2) Preencha uma linha por colaborador " +
                    "(nome, CPF, matrícula, admissão e cargo escolhido na lista do PGR). " +
                    "3) Importe o arquivo preenchido, confira a prévia e confirme.",
                style = MaterialTheme.typography.bodySmall
            )
            FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                PortalOutlinedButton(
                    enabled = !importando,
                    onClick = {
                        runCatching {
                            val path = PlatformFiles.join(PlatformFiles.cacheDir(), FuncionariosPlanilha.NOME_MODELO)
                            PlatformFiles.writeBytes(path, FuncionariosPlanilha.gerarModelo())
                            br.com.htms.inspeccaocampo.platform.BackupSharePoint.semBackup {
                                PlatformFiles.shareFile(path, FuncionariosPlanilha.MIME_XLSX, "Planilha modelo de colaboradores")
                            }
                        }.onFailure { mensagem = "Falha ao gerar a planilha modelo: ${it.message}" }
                    }
                ) { Text("Baixar planilha modelo") }
                PortalButton(
                    enabled = !lendo && !importando,
                    onClick = {
                        scope.launch {
                            lendo = true
                            mensagem = null
                            falhas = emptyList()
                            val doc = PlatformServices.pickPlanilha()
                            if (doc != null) {
                                arquivo = doc.name
                                resultado = FuncionariosPlanilha.importar(doc.name, doc.bytes, existentes) {
                                    PlatformServices.inflateRaw(it)
                                }
                            }
                            lendo = false
                        }
                    }
                ) { Text(if (lendo) "Lendo..." else "Importar planilha") }
            }

            mensagem?.let { Text(it, color = MaterialTheme.colorScheme.primary, fontWeight = FontWeight.SemiBold) }
            falhas.forEach { Text(it, color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodySmall) }

            val r = resultado ?: return@Column
            HorizontalDivider()
            Text("Arquivo: $arquivo", style = MaterialTheme.typography.bodySmall)
            r.erroGeral?.let {
                Text(it, color = MaterialTheme.colorScheme.error)
                return@Column
            }
            val novos = r.linhas.filter { it.valida && it.existente == null }
            val jaCadastrados = r.linhas.filter { it.valida && it.existente != null }
            val comErro = r.linhas.filter { !it.valida }
            Text(
                "${novos.size} novo(s) • ${jaCadastrados.size} já cadastrado(s) • ${comErro.size} com erro",
                fontWeight = FontWeight.SemiBold
            )
            if (jaCadastrados.isNotEmpty()) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.clickable(enabled = !importando) { atualizarExistentes = !atualizarExistentes }
                ) {
                    Checkbox(checked = atualizarExistentes, onCheckedChange = { atualizarExistentes = it }, enabled = !importando)
                    Text("Atualizar os já cadastrados (campos vazios na planilha mantêm o valor atual)")
                }
            }
            if (comErro.isNotEmpty()) {
                Text(
                    "Linhas com erro (não serão importadas; corrija a planilha e importe de novo):",
                    color = MaterialTheme.colorScheme.error,
                    style = MaterialTheme.typography.bodySmall
                )
                comErro.take(50).forEach { l ->
                    Text(
                        "Linha ${l.linha}: ${l.nome.ifBlank { "(sem nome)" }} — ${l.erros.joinToString("; ")}",
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.bodySmall
                    )
                }
                if (comErro.size > 50) Text("... e mais ${comErro.size - 50} linha(s) com erro.", style = MaterialTheme.typography.bodySmall)
            }
            val validas = novos + jaCadastrados
            validas.take(30).forEach { l -> LinhaPrevia(l) }
            if (validas.size > 30) Text("... e mais ${validas.size - 30} colaborador(es).", style = MaterialTheme.typography.bodySmall)

            val alvo = novos + if (atualizarExistentes) jaCadastrados else emptyList()
            if (importando) {
                LinearProgressIndicator(
                    progress = { if (total == 0) 0f else progresso.toFloat() / total },
                    modifier = Modifier.fillMaxWidth()
                )
                Text("Cadastrando $progresso de $total...", style = MaterialTheme.typography.bodySmall)
            }
            PortalButton(
                enabled = alvo.isNotEmpty() && !importando,
                onClick = {
                    scope.launch {
                        importando = true
                        total = alvo.size
                        progresso = 0
                        var ok = 0
                        val erros = ArrayList<String>()
                        for (l in alvo) {
                            val ex = l.existente
                            FuncionariosApi.salvar(
                                nome = l.nome,
                                cpf = l.cpf,
                                admissao = l.admissao.ifBlank { ex?.admissao.orEmpty() },
                                cargoId = l.cargo?.id ?: ex?.cargoId.orEmpty(),
                                cargoNome = l.cargo?.cargo?.nome ?: ex?.cargoNome.orEmpty(),
                                setorGhe = l.cargo?.setorGhe ?: ex?.setorGhe.orEmpty(),
                                matricula = l.matricula.ifBlank { null }
                            ).onSuccess { ok++ }
                                .onFailure { erros += "Linha ${l.linha} (${l.nome}): ${it.message ?: "falha ao salvar"}" }
                            progresso++
                        }
                        importando = false
                        resultado = null
                        falhas = erros
                        mensagem = "$ok colaborador(es) cadastrado(s)/atualizado(s)." +
                            if (erros.isNotEmpty()) " ${erros.size} falharam:" else ""
                        controller.notificarDocumentosAlterados()
                    }
                }
            ) { Text(if (importando) "Cadastrando..." else "Cadastrar ${alvo.size} colaborador(es)") }
        }
    }
}

@Composable
private fun LinhaPrevia(l: LinhaImportacao) {
    Column(Modifier.fillMaxWidth()) {
        Text(
            "${l.nome} — CPF ${Cpf.formatar(l.cpf)}" + if (l.existente != null) " (já cadastrado)" else "",
            fontWeight = FontWeight.SemiBold,
            style = MaterialTheme.typography.bodyMedium
        )
        val detalhes = listOfNotNull(
            l.matricula.takeIf { it.isNotBlank() }?.let { "Matrícula $it" },
            l.admissao.takeIf { it.isNotBlank() }?.let { "Admissão $it" },
            l.cargo?.let { "${it.cargo.nome} (${it.setorGhe})" }
        )
        if (detalhes.isNotEmpty()) Text(detalhes.joinToString(" • "), style = MaterialTheme.typography.bodySmall)
    }
}
